import { Checkbox, Radio, styled, Switch, SwitchProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { StateNames, States } from 'src/Screens/Configurator';
import { checkMinMax } from 'src/utils/3d.utils';
import { createSoles } from 'src/utils/sole.utils';
import {
  changeCustomElementStatus as changeSoleStatus,
  isCustomElementUse as isSoleUse,
} from 'src/utils/common.utils';

type CustomSolesProps = {
  pieces: any[];
  setCustomSoles: (customSoles: any[]) => void;
  customPlatings: any[];
  customSoles: any[];
  customBoxes: any[];
  setValue: <T extends StateNames>(stateName: T, value: States[T]) => void;
  getValue: <T extends StateNames>(stateName: T) => States[T];
};

export const CustomSoles = ({
  pieces,
  setCustomSoles,
  customSoles,
  customBoxes,
  setValue,
  getValue,
  customPlatings,
}: CustomSolesProps) => {
  const [width, setWidth] = useState<string>();
  const [length, setLength] = useState<string>();
  const [error, setError] = useState<string>();
  const [height, setHeight] = useState<string>();

  const [holeWidth, setHoleWidth] = useState<string>();
  const [holeLength, setHoleLength] = useState<string>();
  const [poseX, setPoseX] = useState<string>();
  const [poseZ, setPoseZ] = useState<string>();
  const [frontDepth, setFrontDepth] = useState<string>();

  const [hasCustomFrontDepth, setHasCustomFrontDepth] = useState<boolean>();
  const [holeMode, setHoleMode] = useState<'dimension' | 'pose'>();
  const [mode, setMode] = useState<'closoir' | 'double closoir' | 'tete'>();

  useEffect(() => {
    if (customSoles.length > 0) {
      setWidth(
        String(
          Math.round(
            Number.parseFloat(customSoles[0]?.shapeDetails.monumentWidth) * 100,
          ),
        ),
      );
      setLength(
        String(
          Math.round(
            Number.parseFloat(customSoles[0]?.shapeDetails.monumentLength) *
              100,
          ),
        ),
      );
      setHeight(
        String(
          Math.round(
            Number.parseFloat(customSoles[0]?.shapeDetails.height) * 100,
          ),
        ),
      );
      setHoleWidth(
        String(
          Math.round(
            Number.parseFloat(customSoles[0]?.shapeDetails.holeWidth) * 100,
          ),
        ),
      );
      setHoleLength(
        String(
          Math.round(
            Number.parseFloat(customSoles[0]?.shapeDetails.holeLength) * 100,
          ),
        ),
      );
      setPoseX(
        String(
          Math.round(
            Number.parseFloat(customSoles[0]?.shapeDetails.poseX) * 100,
          ),
        ),
      );
      setPoseZ(
        String(
          Math.round(
            Number.parseFloat(customSoles[0]?.shapeDetails.poseZ) * 100,
          ),
        ),
      );
      setFrontDepth(
        String(
          Math.round(
            Number.parseFloat(customSoles[0]?.shapeDetails.frontDepth) * 100,
          ),
        ),
      );
      if (holeMode === undefined)
        setHoleMode(customSoles[0]?.shapeDetails.holeMode ?? 'pose');
      if (mode === undefined)
        setMode(customSoles[0]?.shapeDetails.mode ?? 'closoir');
      if (hasCustomFrontDepth === undefined)
        setHasCustomFrontDepth(
          customSoles[0]?.shapeDetails.hasCustomFrontDepth ?? false,
        );
    }
  }, [customSoles]);

  const updatePiece = (forceInformation?: {
    orientation?: 'x' | 'z';
    mode?: 'closoir' | 'double closoir' | 'tete';
    customSoles?: any[];
    height?: string;
    frontDepth?: string;
    holeMode?: 'dimension' | 'pose';
    hasCustomFrontDepth?: boolean;
  }) => {
    const heightToTake = forceInformation?.height ?? height;
    setError(undefined);
    createSoles(
      pieces,
      customPlatings,
      customBoxes,
      forceInformation?.customSoles ?? customSoles,
      setCustomSoles,
      {
        length: Number.parseInt(length ?? '0', 10) / 100,
        width: Number.parseInt(width ?? '0', 10) / 100,
        height: Number.parseInt(heightToTake ?? '0', 10) / 100,
        holeWidth: Number.parseInt(holeWidth ?? '0', 10) / 100,
        holeLength: Number.parseInt(holeLength ?? '0', 10) / 100,
        poseX: Number.parseInt(poseX ?? '0', 10) / 100,
        poseZ: Number.parseInt(poseZ ?? '0', 10) / 100,
        orientation: forceInformation?.orientation ?? 'z',
        holeMode: forceInformation?.holeMode ?? holeMode,
        mode: forceInformation?.mode ?? mode,
        hasCustomFrontDepth:
          forceInformation?.hasCustomFrontDepth ?? hasCustomFrontDepth,
        frontDepth: Number.parseInt(frontDepth ?? '0', 10) / 100,
      },
      (lengthToCheck: number) => {
        checkMinMax(lengthToCheck, customSoles[0]?.granit, setError);
      },
    );
  };

  return (
    <div className='h-full w-full'>
      {getValue('hasAdvancedMode') ? (
        <div className='bg-[#D32F2F70] px-3 py-1.5 rounded-xl mb-3'>
          <p className='text-xs'>Attention, le mode avancé est activé.</p>
          <p className='text-xs'>
            Les assemblages de pièces ne sont plus pris en compte. Vous
            modifierez et déplacerez chaque pièce indépendamment des autres.
          </p>
        </div>
      ) : null}

      <div className='flex flex-row justify-between'>
        <div className='flex flex-row'>
          <span className='text-sm text-[#344054] cursor-pointer z-[2]'>
            Dimension
          </span>
          <span className='text-sm text-[#344054] ml-1 mb-2.5 font-bold'>
            Semelle
          </span>
        </div>
        <div className='flex flex-row items-center gap-1.5 ml-0'>
          <span className='text-xs text-[#344054] font-normal'>
            Mode avancé
          </span>
          <IOSSwitch
            checked={getValue('hasAdvancedMode')}
            onChange={() =>
              setValue('hasAdvancedMode', !getValue('hasAdvancedMode'))
            }
          />
        </div>
      </div>

      {error ? (
        <p className="font-['Open_Sans'] font-semibold pt-4 text-red-600 text-sm">
          {error}
        </p>
      ) : null}

      <div className='flex flex-row'>
        <div className='flex flex-row w-[60%]'>
          <div className='flex flex-col'>
            <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
              Longueur totale
            </p>
            <div className='flex flex-row'>
              <input
                className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
                value={length}
                onChange={(e) => setLength(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') updatePiece();
                }}
                onBlur={() => {
                  updatePiece();
                }}
              />
              <div className='flex justify-center items-center m-2.5 text-[#667085]'>
                x
              </div>
            </div>
          </div>
          <div className='flex flex-col'>
            <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
              Largeur totale
            </p>
            <div className='flex flex-row'>
              <input
                className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') updatePiece();
                }}
                onBlur={() => {
                  updatePiece();
                }}
              />
              <div className='flex justify-center items-center m-2.5 text-[#667085]' />
            </div>
          </div>
        </div>
        <div className='flex flex-col w-[40%]'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Epaisseur
          </p>
          <input
            className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') updatePiece();
            }}
            onBlur={() => {
              updatePiece();
            }}
          />
        </div>
      </div>

      <div className='flex flex-row w-full'>
        <div className='flex flex-col mr-2.5 w-[100px] items-start'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Dimension
          </p>
          <Radio
            checked={holeMode === 'dimension'}
            onClick={() => {
              setHoleMode('dimension');
              updatePiece({ holeMode: 'dimension' });
            }}
          />
        </div>
        <div className='flex flex-col'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Profondeur totale trou
          </p>
          <div className='flex flex-row'>
            <input
              className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
              disabled={holeMode !== 'dimension'}
              value={holeLength}
              onChange={(e) => setHoleLength(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece();
              }}
              onBlur={() => {
                updatePiece();
              }}
            />
            <div className='flex justify-center items-center m-2.5 text-[#667085]'>
              x
            </div>
          </div>
        </div>
        <div className='flex flex-col'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Largeur totale trou
          </p>
          <div className='flex flex-row'>
            <input
              className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
              value={holeWidth}
              disabled={holeMode !== 'dimension'}
              onChange={(e) => setHoleWidth(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece();
              }}
              onBlur={() => {
                updatePiece();
              }}
            />
            <div className='flex justify-center items-center m-2.5 text-[#667085]' />
          </div>
        </div>
      </div>

      <div className='flex flex-row w-full'>
        <div className='flex flex-col mr-2.5 w-[100px] items-start'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Pose
          </p>
          <Radio
            checked={holeMode === 'pose'}
            onClick={() => {
              setHoleMode('pose');
              updatePiece({ holeMode: 'pose' });
            }}
          />
        </div>
        <div className='flex flex-col'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Pose Avant / Arrière
          </p>
          <div className='flex flex-row'>
            <input
              className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
              disabled={holeMode !== 'pose'}
              value={poseZ}
              onChange={(e) => setPoseZ(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece();
              }}
              onBlur={() => {
                updatePiece();
              }}
            />
            <div className='flex justify-center items-center m-2.5 text-[#667085]'>
              x
            </div>
          </div>
        </div>
        <div className='flex flex-col'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Pose Gauche / Droite
          </p>
          <div className='flex flex-row'>
            <input
              className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
              disabled={holeMode !== 'pose'}
              value={poseX}
              onChange={(e) => setPoseX(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece();
              }}
              onBlur={() => {
                updatePiece();
              }}
            />
            <div className='flex justify-center items-center m-2.5 text-[#667085]' />
          </div>
        </div>
      </div>

      <div className='flex flex-row'>
        <div className='flex flex-col mr-2.5 w-[100px] items-start'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Tête avant personalisé
          </p>
          <Checkbox
            value={hasCustomFrontDepth}
            checked={hasCustomFrontDepth}
            onClick={() => {
              setHasCustomFrontDepth(!hasCustomFrontDepth);
              updatePiece({ hasCustomFrontDepth: !hasCustomFrontDepth });
            }}
          />
        </div>
        <div className='flex flex-col'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Dimension tête avant
          </p>
          <div className='flex flex-row'>
            <input
              className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
              disabled={!hasCustomFrontDepth}
              value={frontDepth}
              onChange={(e) => setFrontDepth(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') updatePiece({ frontDepth });
              }}
              onBlur={() => {
                updatePiece({ frontDepth });
              }}
            />
          </div>
        </div>
      </div>
      <div className='flex flex-row'>
        <div className='relative mt-[42px] w-[188px] min-w-[188px] max-w-[188px] h-[193px] min-h-[193px] max-h-[193px]'>
          <div className='flex flex-row h-full'>
            <img
              src='/icons/sole-left.png'
              className='cursor-pointer'
              style={{
                width: 82,
                height: 105,
                filter: isSoleUse(customSoles, 'left')
                  ? ''
                  : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%)',
              }}
              onClick={() => {
                changeSoleStatus('left', customSoles, setCustomSoles);
                updatePiece({
                  customSoles: customSoles.map((el) => {
                    if (el.key === 'left')
                      return {
                        ...el,
                        isActive: !el.isActive,
                      };
                    return el;
                  }),
                });
              }}
            />
            <div className='flex flex-col justify-between'>
              <img
                src='/icons/sole-back.png'
                className='cursor-pointer ml-[5px] mt-[-10px] z-[2] w-[80px] h-[29px] min-w-[80px] min-h-[29px] max-w-[80px] max-h-[29px]'
                style={{
                  filter: isSoleUse(customSoles, 'back')
                    ? ''
                    : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%)',
                }}
                onClick={() => {
                  changeSoleStatus('back', customSoles, setCustomSoles);
                  updatePiece({
                    customSoles: customSoles.map((el) => {
                      if (el.key === 'back')
                        return {
                          ...el,
                          isActive: !el.isActive,
                        };
                      return el;
                    }),
                  });
                }}
              />
              <img
                src='/icons/sole-front.png'
                className='cursor-pointer ml-[-70px] mb-[35px] w-[117px] h-[53px] min-w-[117px] min-h-[53px] max-w-[117px] max-h-[53px]'
                style={{
                  filter: isSoleUse(customSoles, 'front')
                    ? ''
                    : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%)',
                }}
                onClick={() => {
                  changeSoleStatus('front', customSoles, setCustomSoles);
                  updatePiece({
                    customSoles: customSoles.map((el) => {
                      if (el.key === 'front')
                        return {
                          ...el,
                          isActive: !el.isActive,
                        };
                      return el;
                    }),
                  });
                }}
              />
            </div>
            <img
              src='/icons/sole-right.png'
              className='cursor-pointer ml-[-30px] mt-[15px]'
              style={{
                width: 50,
                height: 130,
                filter: isSoleUse(customSoles, 'right')
                  ? ''
                  : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%)',
              }}
              onClick={() => {
                changeSoleStatus('right', customSoles, setCustomSoles);
                updatePiece({
                  customSoles: customSoles.map((el) => {
                    if (el.key === 'right')
                      return {
                        ...el,
                        isActive: !el.isActive,
                      };
                    return el;
                  }),
                });
              }}
            />
          </div>
          <p className='text-sm text-center italic text-[#667085]'>
            Cliquez sur une semelle pour la retirer ou l&apos;ajouter.
          </p>
        </div>
        <div className='flex flex-col w-full justify-center p-5 gap-3'>
          <div className='flex flex-row items-center'>
            <input
              type='checkbox'
              className='w-[1.3em] h-[1.3em] bg-white rounded-full align-middle border border-[#ddd] appearance-none outline-none cursor-pointer p-2 mr-2 checked:bg-[#BC9A67] checked:border-[#BC9A67]'
              checked={mode === 'tete'}
              onChange={() => {
                setMode('tete');
                updatePiece({ mode: 'tete' });
              }}
            />
            <span className='text-sm text-[#273135]'>
              Tête avant / tête arrière
            </span>
          </div>
          <div className='flex flex-row items-center'>
            <input
              type='checkbox'
              className='w-[1.3em] h-[1.3em] bg-white rounded-full align-middle border border-[#ddd] appearance-none outline-none cursor-pointer p-2 mr-2 checked:bg-[#BC9A67] checked:border-[#BC9A67]'
              checked={mode === 'closoir'}
              onChange={() => {
                setMode('closoir');
                updatePiece({ mode: 'closoir' });
              }}
            />
            <span className='text-sm text-[#273135]'>Closoir arrière</span>
          </div>
          <div className='flex flex-row items-center'>
            <input
              type='checkbox'
              className='w-[1.3em] h-[1.3em] bg-white rounded-full align-middle border border-[#ddd] appearance-none outline-none cursor-pointer p-2 mr-2 checked:bg-[#BC9A67] checked:border-[#BC9A67]'
              checked={mode === 'double closoir'}
              onChange={() => {
                setMode('double closoir');
                updatePiece({ mode: 'double closoir' });
              }}
            />
            <span className='text-sm text-[#273135]'>Double closoir</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#BC9A67' : '#BC9A67',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#BC9A67',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));
